/* The theatre's hero — KANON `Cwiczenia` 17.09.2026, `2-karta.html#e`'s `hero()` at its variant-e
 * settings: no name on the picture (the bar above carries it), no time chip (deviation 2). The scrim
 * with the type mark and the „Twoje" badge went with the polish of 4 November 2026 — the bar prints
 * both — and the play button stands on its own plate.
 *
 * The box is settled by the PANEL: `flex: 1` over a floor, so the picture takes whatever the
 * similar strip under it leaves and `object-fit: cover` frames it. Nothing here depends on the
 * file's own dimensions, which is what makes a slow photograph a change of ink.
 *
 * Every text leaf states its line-height. */

.hero {
  position: relative;
  flex: 1;
  min-height: var(--h-ex-theatre-hero);
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-sm);
}

/* Both fillers draw the same box. `object-fit: cover` is `photoBox`'s on the `<img>`; the `<video>`
 * states its own, because it is not an `<img>` and does not inherit that stylesheet. */
.still,
.film {
  position: absolute;
  inset: var(--space-0);
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: var(--bg-sunken);
}

/* No photograph and no film. A drawn tile and a word, never an empty box — an empty box on a card
 * this size reads as a render that failed. */
.tile {
  position: absolute;
  inset: var(--space-0);
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  align-items: center;
  justify-content: center;
  color: var(--text-3);
}

.tileGlyph {
  display: block;
  width: 76px;
  height: 76px;
}

/* A failed upload's sentence, in the tile's place. Readable ink (`--text-2`), not the ornament's
 * `--text-3`: it is a sentence the trainer has to read. */
.tileNote {
  max-width: 36ch;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-note);
  color: var(--text-2);
  text-align: center;
}

/* The same sentence over a picture: the badge's white plate, in the corner the scrim leaves free.
 * Theme-independent for the badge's reason — it sits on a photograph. A caption, never a target:
 * the hero's own controls stay clickable under it. */
.plateNote {
  position: absolute;
  pointer-events: none;
  top: var(--space-4);
  left: var(--space-5);
  max-width: 36ch;
  padding: var(--space-6px) var(--space-2);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-note);
  color: var(--text-on-plate);
  background: var(--plate-on-photo);
  border-radius: var(--radius-xs);
}

.tileWord {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-3);
}

/* ── The play button's dock ─────────────────────────────────────────────────────────────────── */

/* `pointer-events: none`, so the dock cannot swallow a press on the browser's own control strip
 * underneath it. The play button takes its own back — see `.play`. */
.playDock {
  position: absolute;
  bottom: var(--space-0);
  left: var(--space-0);
  padding: var(--space-4) var(--space-5);
  pointer-events: none;
}

/* The only thing in the scrim that takes a press back. */
.play {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-ex-theatre-play);
  height: var(--sz-ex-theatre-play);
  margin-bottom: var(--space-2px);
  line-height: var(--lh-none);
  pointer-events: auto;
  cursor: pointer;
  color: var(--text-on-plate);
  background: var(--plate-on-photo);
  border: none;
  border-radius: var(--r-pill);
}

/* The ring is white because the plate is: `--accent` on a white disc over a dark photograph is the
 * one focus treatment on this surface that would disappear, and a coloured glow is banned outright. */
.play:focus-visible {
  outline: var(--border-focus) solid var(--text-on-photo);
  outline-offset: var(--space-2px);
}

.playGlyph {
  display: block;
  width: 15px;
  height: 15px;

  /* The triangle's optical centre is right of its bounding box's. */
  margin-left: var(--space-2px);
}
